import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { getMentorNote } from "../../lib/firebase";
import { saveMentorNote } from "../../lib/functions";

// ---------------------------------------------------------------------------
// Styled components — matching --nd-* tokens used in PathDashboard + JuniorDrawer
// ---------------------------------------------------------------------------

const Editor = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Label = styled.label`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  display: block;
  margin-bottom: 4px;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 68px;
  background: var(--nd-bg, #0d1117);
  border: 1px solid var(--nd-border);
  border-radius: 4px;
  color: var(--nd-text-primary, #c9d1d9);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  padding: 8px 10px;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--nd-accent, #58a6ff);
  }
`;

const GoalsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GoalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GoalSelect = styled.select`
  background: var(--nd-bg, #0d1117);
  border: 1px solid var(--nd-border);
  border-radius: 4px;
  color: var(--nd-text-primary, #c9d1d9);
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  padding: 5px 6px;
  flex-shrink: 0;

  &:focus {
    outline: none;
    border-color: var(--nd-accent, #58a6ff);
  }
`;

const GoalInput = styled.input`
  flex: 1;
  background: var(--nd-bg, #0d1117);
  border: 1px solid var(--nd-border);
  border-radius: 4px;
  color: var(--nd-text-primary, #c9d1d9);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  padding: 5px 8px;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: var(--nd-accent, #58a6ff);
  }
`;

const STATUS_CYCLE = ["not_started", "in_progress", "done"];
const STATUS_LABELS = {
  not_started: "not started",
  in_progress: "in progress",
  done: "done",
};

const StatusBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.04em;
  white-space: nowrap;
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid var(--nd-border);
  background: transparent;
  color: ${({ status }) =>
    status === "done"
      ? "var(--nd-success, #4ade80)"
      : status === "in_progress"
      ? "var(--nd-accent, #58a6ff)"
      : "var(--nd-text-disabled)"};
  cursor: pointer;
  transition: opacity 0.1s;

  &:hover { opacity: 0.75; }
`;

const RemoveBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  flex-shrink: 0;
  background: transparent;
  border: 1px solid var(--nd-border);
  border-radius: 4px;
  color: var(--nd-text-disabled);
  padding: 4px 8px;
  cursor: pointer;
  line-height: 1;

  &:hover { color: var(--nd-error, #f87171); border-color: var(--nd-error, #f87171); }
`;

const AddGoalBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  padding: 5px 12px;
  border-radius: 4px;
  border: 1px dashed var(--nd-border);
  background: transparent;
  color: var(--nd-text-disabled);
  cursor: pointer;
  align-self: flex-start;
  transition: color 0.1s, border-color 0.1s;

  &:hover:not(:disabled) { color: var(--nd-text-primary); border-color: var(--nd-text-secondary); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SaveBtn = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  padding: 6px 14px;
  border-radius: 4px;
  border: 1px solid var(--nd-accent, #58a6ff);
  background: transparent;
  color: var(--nd-accent, #58a6ff);
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) { background: rgba(88,166,255,0.08); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const SaveStatus = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  color: ${({ ok }) => (ok ? "var(--nd-success, #4ade80)" : "var(--nd-error, #f87171)")};
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MAX_GOALS = 12;

// Mint a stable client-only id for each in-memory goal row using a monotonic
// counter. This avoids key collisions when rows are removed mid-list.
let _goalIdCounter = 0;
const nextGoalId = () => ++_goalIdCounter;

const blankGoal = () => ({ _id: nextGoalId(), horizon: "3mo", text: "", status: "not_started" });

// Attach stable _ids to goals loaded from Firestore (they have none).
const withIds = (goals) => (Array.isArray(goals) ? goals.map((g) => ({ _id: nextGoalId(), ...g })) : []);

// Strip the client-only _id before sending to the server.
const stripId = ({ _id, ...rest }) => rest; // eslint-disable-line no-unused-vars

const MentorNotesEditor = ({ uid }) => {
  // undefined = loading, then string value
  const [comment, setComment] = useState(undefined);
  const [plan, setPlan] = useState(undefined);
  const [goals, setGoals] = useState(undefined); // undefined = loading, then []
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // "saved" | "error" | null

  // Timer ref — used to clear the "saved"/"error" status timeout on re-save or unmount.
  const statusTimerRef = useRef(null);

  const loaded = comment !== undefined;

  // Cleanup the status timer on unmount to prevent setState-after-unmount.
  useEffect(() => () => { clearTimeout(statusTimerRef.current); }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !uid) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const note = await getMentorNote(uid);
        if (!cancelled) {
          setComment((note && note.comment) || "");
          setPlan((note && note.plan) || "");
          setGoals(withIds(note && note.goals));
        }
      } catch (_) {
        if (!cancelled) {
          setComment("");
          setPlan("");
          setGoals([]);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [uid]);

  const cycleStatus = (id) => {
    setGoals((prev) => prev.map((g) => {
      if (g._id !== id) return g;
      const pos = STATUS_CYCLE.indexOf(g.status || "not_started");
      return { ...g, status: STATUS_CYCLE[(pos + 1) % STATUS_CYCLE.length] };
    }));
  };

  const updateGoal = (id, field, value) => {
    setGoals((prev) => prev.map((g) => (g._id === id ? { ...g, [field]: value } : g)));
  };

  const removeGoal = (id) => {
    setGoals((prev) => prev.filter((g) => g._id !== id));
  };

  const addGoal = () => {
    setGoals((prev) => (prev.length < MAX_GOALS ? [...prev, blankGoal()] : prev));
  };

  const onSave = async () => {
    if (!loaded || saving) return;
    setSaving(true);
    // Clear any pending status timer before starting a new save.
    clearTimeout(statusTimerRef.current);
    setSaveStatus(null);
    try {
      const filteredGoals = (goals || [])
        .filter((g) => g.text && g.text.trim() !== "")
        .map(stripId); // strip client-only _id; server expects {horizon,text,status}
      await saveMentorNote({ uid, comment: comment || "", plan: plan || "", goals: filteredGoals });
      setSaveStatus("saved");
      statusTimerRef.current = setTimeout(() => setSaveStatus(null), 3000);
    } catch (_) {
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Editor>
      {/* Comment */}
      <div>
        <Label htmlFor={`mne-comment-${uid}`}>Comment</Label>
        <Textarea
          id={`mne-comment-${uid}`}
          value={loaded ? comment : ""}
          placeholder={!loaded ? "Loading…" : "Manager notes for this junior…"}
          disabled={!loaded || saving}
          onChange={(e) => setComment(e.target.value)}
        />
      </div>

      {/* Plan */}
      <div>
        <Label htmlFor={`mne-plan-${uid}`}>Plan</Label>
        <Textarea
          id={`mne-plan-${uid}`}
          value={loaded ? plan : ""}
          placeholder={!loaded ? "Loading…" : "Development plan…"}
          disabled={!loaded || saving}
          onChange={(e) => setPlan(e.target.value)}
        />
      </div>

      {/* Goals */}
      <div>
        <Label as="div">Goals</Label>
        <GoalsSection>
          {loaded && goals && goals.map((g) => (
            <GoalRow key={g._id}>
              <GoalSelect
                value={g.horizon}
                disabled={saving}
                onChange={(e) => updateGoal(g._id, "horizon", e.target.value)}
                aria-label="Horizon"
              >
                <option value="3mo">3mo</option>
                <option value="6mo">6mo</option>
                <option value="12mo">12mo</option>
                <option value="beyond">beyond</option>
              </GoalSelect>
              <GoalInput
                value={g.text}
                disabled={saving}
                placeholder="Goal description…"
                onChange={(e) => updateGoal(g._id, "text", e.target.value)}
                aria-label="Goal text"
              />
              <StatusBtn
                type="button"
                status={g.status}
                disabled={saving}
                onClick={() => cycleStatus(g._id)}
                title="Click to cycle status"
              >
                {STATUS_LABELS[g.status] || g.status}
              </StatusBtn>
              <RemoveBtn
                type="button"
                disabled={saving}
                onClick={() => removeGoal(g._id)}
                aria-label="Remove goal"
              >
                ×
              </RemoveBtn>
            </GoalRow>
          ))}
          {!loaded && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "var(--nd-text-disabled)" }}>Loading…</div>}
          <AddGoalBtn
            type="button"
            disabled={!loaded || saving || (goals && goals.length >= MAX_GOALS)}
            onClick={addGoal}
          >
            + add goal
          </AddGoalBtn>
        </GoalsSection>
      </div>

      {/* Save footer */}
      <Footer>
        <SaveBtn type="button" disabled={!loaded || saving} onClick={onSave}>
          {saving ? "Saving…" : "Save"}
        </SaveBtn>
        {saveStatus === "saved" && <SaveStatus ok>Saved</SaveStatus>}
        {saveStatus === "error" && <SaveStatus>Error — try again</SaveStatus>}
      </Footer>
    </Editor>
  );
};

export default MentorNotesEditor;
